// =====================================================
// EVENT ATTENDANCE SERVICE
// Stage 3: Attendance Tracking
// Service for managing participant attendance
// =====================================================

import { supabase } from '@/supabase.config';

// ===================
// TYPES
// ===================

export interface CheckInData {
  participant_id: string;
  checked_in_by?: string;
}

export interface BulkCheckInData {
  participant_ids: string[];
  checked_in_by?: string;
}

export interface AttendanceStats {
  total: number;
  attended: number;
  absent: number;
  pending: number;
  attendance_rate: number;
}

// ===================
// CHECK-IN FUNCTIONS
// ===================

/**
 * Check-in a single participant
 * Updates attendance_status to 'ATTENDED' and records check-in time
 */
export async function checkInParticipant(data: CheckInData): Promise<void> {
  const { participant_id, checked_in_by } = data;

  const updateData: Record<string, unknown> = {
    attendance_status: 'ATTENDED',
    checked_in_at: new Date().toISOString(),
  };

  if (checked_in_by) {
    updateData.checked_in_by = checked_in_by;
  }

  const { error } = await supabase
    .from('event_participants')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(updateData as any)
    .eq('id', participant_id);

  if (error) {
    throw new Error(`Error checking in participant: ${error.message}`);
  }
}

/**
 * Bulk check-in multiple participants
 * Checks in all participants in a single operation
 */
export async function bulkCheckInParticipants(data: BulkCheckInData): Promise<{
  success: number;
  failed: number;
  errors: string[];
}> {
  const { participant_ids, checked_in_by } = data;
  const results = {
    success: 0,
    failed: 0,
    errors: [] as string[],
  };

  const updateData: Record<string, unknown> = {
    attendance_status: 'ATTENDED',
    checked_in_at: new Date().toISOString(),
  };

  if (checked_in_by) {
    updateData.checked_in_by = checked_in_by;
  }

  try {
    const { error, count } = await supabase
      .from('event_participants')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(updateData as any)
      .in('id', participant_ids);

    if (error) {
      results.failed = participant_ids.length;
      results.errors.push(error.message);
    } else {
      results.success = count || participant_ids.length;
    }
  } catch (error) {
    results.failed = participant_ids.length;
    results.errors.push(error instanceof Error ? error.message : 'Unknown error');
  }

  return results;
}

/**
 * Mark participant as absent
 */
export async function markParticipantAbsent(participant_id: string): Promise<void> {
  const { error } = await supabase
    .from('event_participants')
    .update({
      attendance_status: 'ABSENT',
      checked_in_at: null,
      checked_in_by: null,
    })
    .eq('id', participant_id);

  if (error) {
    throw new Error(`Error marking participant as absent: ${error.message}`);
  }
}

/**
 * Reset attendance status to pending
 */
export async function resetAttendanceStatus(participant_id: string): Promise<void> {
  const { error } = await supabase
    .from('event_participants')
    .update({
      attendance_status: 'PENDING',
      checked_in_at: null,
      checked_in_by: null,
    })
    .eq('id', participant_id);

  if (error) {
    throw new Error(`Error resetting attendance status: ${error.message}`);
  }
}

// ===================
// STATISTICS
// ===================

/**
 * Get attendance statistics for an event
 */
export async function getAttendanceStats(event_id: string): Promise<AttendanceStats> {
  const { data, error } = await supabase
    .from('event_participants')
    .select('attendance_status')
    .eq('event_id', event_id);

  if (error) {
    throw new Error(`Error fetching attendance stats: ${error.message}`);
  }

  const stats: AttendanceStats = {
    total: data.length,
    attended: 0,
    absent: 0,
    pending: 0,
    attendance_rate: 0,
  };

  data.forEach((participant) => {
    switch (participant.attendance_status) {
      case 'ATTENDED':
        stats.attended++;
        break;
      case 'ABSENT':
        stats.absent++;
        break;
      case 'PENDING':
      default:
        stats.pending++;
        break;
    }
  });

  stats.attendance_rate = stats.total > 0 ? (stats.attended / stats.total) * 100 : 0;

  return stats;
}

/**
 * Bulk check-in all pending participants for an event
 */
export async function bulkCheckInAllPending(
  event_id: string,
  checked_in_by?: string
): Promise<{
  success: number;
  failed: number;
  errors: string[];
}> {
  // Get all pending participants
  const { data: participants, error: fetchError } = await supabase
    .from('event_participants')
    .select('id')
    .eq('event_id', event_id)
    .eq('attendance_status', 'PENDING');

  if (fetchError) {
    throw new Error(`Error fetching pending participants: ${fetchError.message}`);
  }

  if (!participants || participants.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  const participant_ids = participants.map((p) => p.id);
  const result = await bulkCheckInParticipants({ participant_ids, checked_in_by });

  return {
    success: result.success,
    failed: result.failed,
    errors: result.errors,
  };
}
